import { useState, type FormEvent } from 'react';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { feedUrl, useChannelHolds, useChannelMutation, useChannels, type Channel, type ChannelLink } from '../api/channels';
import { formatDate, formatDateTime } from '../format';
import { Badge, Button, Empty, ErrorNote, Field, Loading, Modal, useReport, useToast } from '../ui/kit';
import { PageHead } from './shared';

export function Channels() {
  const { can } = useAuth();
  const q = useChannels();
  const holds = useChannelHolds();
  const [adding, setAdding] = useState(false);
  const manage = can('channel.manage');

  return (
    <div className="page">
      <PageHead eyebrow="Selling rooms elsewhere" title="Channels">
        {manage ? <Button variant="primary" onClick={() => setAdding(true)}>Add a channel</Button> : null}
      </PageHead>

      <section className="card stack" aria-labelledby="how">
        <h2 className="h2" id="how">How it keeps the calendars in step</h2>
        <p className="small">
          For each website that sells your rooms (Booking.com, Airbnb, Expedia…), two calendar addresses are exchanged.
          <strong> Theirs</strong> lists the reservations they have taken: we read it every 15 minutes, and each one takes a room off sale here.
          <strong> Ours</strong> lists the nights you have no room left: they read it, and stop selling those nights. Money, guests and payment stay with the channel; a reservation made there appears here only as a room taken.
        </p>
        <p className="hint">The websites read calendars every few hours, not instantly, so two guests can still book the last room within that gap. Keep one room back for the channels if that worries you, and check the list below for anything marked overbooked.</p>
      </section>

      {q.isLoading ? (
        <Loading rows={2} />
      ) : q.error ? (
        <ErrorNote error={q.error} retry={() => void q.refetch()} />
      ) : (q.data?.channels ?? []).length === 0 ? (
        <div className="card flat"><Empty icon="inbox" title="No channels yet">{manage ? 'Add the first one, then link each room type to it.' : 'A manager sets these up.'}</Empty></div>
      ) : (
        q.data!.channels.map((c) => <ChannelCard key={c.id} channel={c} roomTypes={q.data!.roomTypes} manage={manage} />)
      )}

      <section className="stack" aria-labelledby="held">
        <h2 className="h2" id="held">Reservations made on channels</h2>
        {holds.isLoading ? (
          <Loading rows={1} />
        ) : (holds.data ?? []).length === 0 ? (
          <p className="small muted">None coming up.</p>
        ) : (
          <div className="list">
            {holds.data!.map((h) => (
              <div key={h.id} className="item">
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <span className="title">{h.roomType}</span>
                  <span className="row">
                    {h.oversold ? <Badge tone="bad">Overbooked</Badge> : null}
                    <Badge>{h.channel}</Badge>
                  </span>
                </div>
                <div className="small muted">{formatDate(h.from)} → {formatDate(h.to)}{h.summary ? ` · ${h.summary}` : ''}</div>
                {h.oversold ? <div className="hint">There are more reservations than rooms on some of these nights. Decide who to move or send elsewhere, and tell the channel.</div> : null}
              </div>
            ))}
          </div>
        )}
      </section>

      {adding ? <AddChannel onClose={() => setAdding(false)} /> : null}
    </div>
  );
}

function ChannelCard({ channel, roomTypes, manage }: { channel: Channel; roomTypes: { id: string; name: string }[]; manage: boolean }) {
  const toast = useToast();
  const report = useReport();
  const [linking, setLinking] = useState(false);
  const toggle = useChannelMutation(() => api(`/channels/${channel.id}`, { method: 'PATCH', body: { active: !channel.active } }));
  const free = roomTypes.filter((t) => !channel.links.some((l) => l.roomTypeId === t.id));

  return (
    <section className="card stack" aria-label={channel.name}>
      <div className="card-head">
        <h2 className="h2">{channel.name}</h2>
        <span className="row">
          {!channel.active ? <Badge tone="warn">Switched off</Badge> : null}
          {manage ? <Button variant="ghost" loading={toggle.isPending} onClick={() => toggle.mutate(undefined, { onError: report, onSuccess: () => toast.ok(channel.active ? 'Switched off: its reservations no longer hold rooms' : 'Switched on') })}>{channel.active ? 'Switch off' : 'Switch on'}</Button> : null}
        </span>
      </div>
      {channel.links.length === 0 ? <p className="small muted">No room types linked yet.</p> : null}
      {channel.links.map((l) => <LinkRow key={l.id} link={l} manage={manage} />)}
      {manage && free.length > 0 ? <Button onClick={() => setLinking(true)}>Link a room type</Button> : null}
      {linking ? <LinkDialog channel={channel} roomTypes={free} onClose={() => setLinking(false)} /> : null}
    </section>
  );
}

function LinkRow({ link, manage }: { link: ChannelLink; manage: boolean }) {
  const toast = useToast();
  const report = useReport();
  const [url, setUrl] = useState(link.importUrl && link.importUrl !== 'set' ? link.importUrl : '');
  const save = useChannelMutation(() => api(`/channel-links/${link.id}`, { method: 'PATCH', body: { importUrl: url.trim() || null } }));
  const sync = useChannelMutation(() => api<{ added: number; updated: number; released: number; conflicts: number }>(`/channel-links/${link.id}/sync`, { method: 'POST', body: {} }));
  const rotate = useChannelMutation(() => api(`/channel-links/${link.id}/rotate`, { method: 'POST', body: {} }));
  const onOff = useChannelMutation(() => api(`/channel-links/${link.id}`, { method: 'PATCH', body: { active: !link.active } }));
  const [confirmRotate, setConfirmRotate] = useState(false);
  const changed = (link.importUrl && link.importUrl !== 'set' ? link.importUrl : '') !== url.trim();
  const full = link.exportPath ? feedUrl(link.exportPath) : '';

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(full);
      toast.ok('Address copied');
    } catch {
      toast.fail('Could not copy: select the address and copy it by hand');
    }
  };

  return (
    <div className="item">
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <span className="title">{link.roomType}</span>
        <span className="row">
          {!link.active ? <Badge tone="warn">Off</Badge> : null}
          {link.upcoming > 0 ? <Badge tone="info">{link.upcoming} coming up</Badge> : null}
          {link.lastImportOk === true ? <Badge tone="good">Read OK</Badge> : link.lastImportOk === false ? <Badge tone="bad">Could not read</Badge> : <Badge>Not read yet</Badge>}
        </span>
      </div>
      <div className="small muted">
        {link.lastImportAt ? `Last read ${formatDateTime(link.lastImportAt)}${link.lastImportMessage ? `: ${link.lastImportMessage}` : ''}` : 'Their calendar has not been read yet.'}
        {link.lastExportAt ? ` · they last read ours ${formatDateTime(link.lastExportAt)}` : ''}
      </div>
      {manage ? (
        <>
          <form className="stack" onSubmit={(e: FormEvent) => { e.preventDefault(); if (changed) save.mutate(undefined, { onError: report, onSuccess: () => toast.ok('Calendar address saved') }); }}>
            <Field id={`iu-${link.id}`} label="Their calendar address (paste it from the channel)" hint="It starts with https://. Booking.com, Airbnb and Expedia each call it the iCal or calendar export link.">
              <input id={`iu-${link.id}`} className="input" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…" autoComplete="off" />
            </Field>
            <div className="row">
              <Button type="submit" disabled={!changed} loading={save.isPending}>Save address</Button>
              <Button type="button" disabled={!link.importUrl || changed} loading={sync.isPending} onClick={() => sync.mutate(undefined, { onError: report, onSuccess: (r) => toast.ok(`Read: ${r.added} new, ${r.updated} changed, ${r.released} released${r.conflicts ? `, ${r.conflicts} overbooked` : ''}`) })}>Read it now</Button>
            </div>
          </form>
          <Field id={`ex-${link.id}`} label="Our calendar address (paste it into the channel)" hint="Secret: anyone with it can see which nights you are sold out. Do not post it publicly.">
            <div className="row">
              <input id={`ex-${link.id}`} className="input" readOnly value={full} onFocus={(e) => e.currentTarget.select()} />
              <Button type="button" onClick={() => void copy()}>Copy</Button>
            </div>
          </Field>
          <div className="row">
            <Button variant="ghost" onClick={() => setConfirmRotate(true)}>Make a new secret address</Button>
            <Button variant="ghost" loading={onOff.isPending} onClick={() => onOff.mutate(undefined, { onError: report })}>{link.active ? 'Switch off this room type' : 'Switch on'}</Button>
          </div>
          <Modal open={confirmRotate} onClose={() => setConfirmRotate(false)} title="Make a new secret address?">
            <p>The old address stops working at once. You must paste the new one into {link.roomType} on the channel, or the channel will not see your sold-out nights.</p>
            <div className="modal-foot">
              <Button onClick={() => setConfirmRotate(false)}>Keep the old one</Button>
              <Button variant="danger" loading={rotate.isPending} onClick={() => rotate.mutate(undefined, { onError: report, onSuccess: () => { toast.ok('New address made'); setConfirmRotate(false); } })}>Make a new one</Button>
            </div>
          </Modal>
        </>
      ) : null}
    </div>
  );
}

function AddChannel({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const report = useReport();
  const [name, setName] = useState('');
  const make = useChannelMutation(() => api('/channels', { method: 'POST', body: { name: name.trim() } }));
  return (
    <Modal open onClose={onClose} title="Add a channel">
      <form className="stack" onSubmit={(e: FormEvent) => { e.preventDefault(); if (name.trim().length >= 2) make.mutate(undefined, { onError: report, onSuccess: () => { toast.ok(`${name.trim()} added`); onClose(); } }); }}>
        <Field id="cn" label="Name of the website"><input id="cn" className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Booking.com" autoFocus /></Field>
        <div className="modal-foot">
          <Button type="button" onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" disabled={name.trim().length < 2} loading={make.isPending}>Add</Button>
        </div>
      </form>
    </Modal>
  );
}

function LinkDialog({ channel, roomTypes, onClose }: { channel: Channel; roomTypes: { id: string; name: string }[]; onClose: () => void }) {
  const toast = useToast();
  const report = useReport();
  const [roomTypeId, setRoomTypeId] = useState(roomTypes[0]?.id ?? '');
  const [url, setUrl] = useState('');
  const make = useChannelMutation(() => api(`/channels/${channel.id}/links`, { method: 'POST', body: { roomTypeId, ...(url.trim() ? { importUrl: url.trim() } : {}) } }));
  return (
    <Modal open onClose={onClose} title={`Link a room type to ${channel.name}`}>
      <form className="stack" onSubmit={(e: FormEvent) => { e.preventDefault(); if (roomTypeId) make.mutate(undefined, { onError: report, onSuccess: () => { toast.ok('Linked'); onClose(); } }); }}>
        <Field id="lr" label="Room type">
          <select id="lr" className="select" value={roomTypeId} onChange={(e) => setRoomTypeId(e.target.value)}>
            {roomTypes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </Field>
        <Field id="lu" label="Their calendar address (optional now)" hint="You can add it later. Our own calendar address is made for you once you link.">
          <input id="lu" className="input" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…" autoComplete="off" />
        </Field>
        <div className="modal-foot">
          <Button type="button" onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" disabled={!roomTypeId} loading={make.isPending}>Link</Button>
        </div>
      </form>
    </Modal>
  );
}
